import { hashToPostgrest } from '@/utils'
import axios, { AxiosInstance, AxiosPromise } from 'axios'
import { filter, find, merge, omit } from 'lodash'
import Vue from 'vue'
import { ActionTree, GetterTree, Module, MutationTree, Payload } from 'vuex'

export interface Entity {
  id: string | number
  [key: string]: any
}

export interface ModuleState {
  loading: boolean
  entities: {
    [key: string]: Entity
  }
  url?: string
}

export type RootState = any

interface AddMutation extends Payload {
  type: 'add'
  entities: Entity[]
}

interface ResetMutation extends Payload {
  type: 'reset'
}

interface DropMutation extends Payload {
  type: 'drop'
  entities: Entity[]
}

interface SetupMutation extends Payload {
  type: 'setup'
  url: string
}

type Mutation = AddMutation | DropMutation | ResetMutation | SetupMutation

interface FetchAction extends Payload {
  type: 'fetch'
  where?: {
    [key: string]: string | number | boolean | null | Date
  }
}

interface SaveAction extends Payload {
  type: 'save'
  entity: Entity
  updateEntity: boolean
}

interface DeleteAction extends Payload {
  type: 'delete'
  entity: Entity
}

type Action = FetchAction | SaveAction | DeleteAction

export const mutations: MutationTree<ModuleState> = {
  setLoading(state, payload: boolean) {
    state.loading = payload
  },
  add(state, { entities }: AddMutation) {
    entities.forEach(entity => Vue.set(state.entities, entity.id, entity))
  },
  drop(state, { entities }: DropMutation) {
    entities.forEach(entity => Vue.delete(state.entities, entity.id))
  },
  reset(state, payload: ResetMutation) {
    state.loading = false
    state.entities = {}
  },
  setup(state, { url }: SetupMutation) {
    state.url = url
  },
}

export const actions: ActionTree<ModuleState, RootState> = {
  USER_LOGGED_OUT: {
    root: true,
    handler({ commit }) {
      commit<Mutation>({ type: 'reset' })
    },
  },
  USER_SIGNED_IN: {
    root: true,
    handler({ dispatch }) {
      dispatch<Action>({ type: 'fetch' })
    },
  },
  delete(
    { state: { url }, commit, rootGetters },
    { entity: { id } }: DeleteAction
  ): Promise<Entity[]> {
    if (!url) {
      throw new Error('state.url must be defined')
    }
    return (rootGetters.http as AxiosInstance)
      .delete<Entity[]>(url, {
        headers: {
          Prefer: 'return=representation',
        },
        params: {
          id: `eq.${id}`,
        },
      })
      .then(({ data: deletedEntities }) => {
        commit<Mutation>({ type: 'drop', entities: deletedEntities })
        return deletedEntities
      })
  },
  fetch({ state: { url }, commit, rootGetters }, { where }: FetchAction): Promise<Entity[]> {
    if (!url) {
      throw new Error('state.url must be defined')
    }
    const params = where ? hashToPostgrest(where) : {}
    commit('setLoading', true)
    return (rootGetters.http as AxiosInstance)
      .get<Entity[]>(url, { params })
      .then(({ data: entities }) => {
        commit<Mutation>({ type: 'add', entities })
        return entities
      })
      .finally(() => {
        commit('setLoading', false)
      })
  },
  save(
    { state: { url }, commit, rootGetters },
    { entity, updateEntity = false }: SaveAction
  ): Promise<Entity> {
    if (!url) {
      throw new Error('state.url must be defined')
    }
    console.log('step 2, before post', entity)
    return (rootGetters.http as AxiosInstance)
      .post<Entity[]>(url, entity, {
        headers: {
          Prefer: 'return=representation, resolution=merge-duplicates',
        },
      })
      .then(({ data: updatedEntities }) => {
        console.log('step 3, after post', updatedEntities)
        if (updatedEntities.length !== 1) {
          throw new Error('expected exactly one result')
        }
        commit<Mutation>({ type: 'add', entities: updatedEntities })
        const updatedEntity = updatedEntities[0]
        if (updateEntity) {
          merge(entity, updatedEntity)
          return entity
        } else {
          return updatedEntity
        }
      })
  },
}

export const module: Module<ModuleState, RootState> = {
  actions,
  getters: {
    loading: state => state.loading,
    entities: state => Object.values(state.entities),
    entitiesById: state => state.entities,
    filterBy: (state, getters) => (predicate: any) => {
      return filter(getters.entities, predicate)
    },
    findBy: (state, getters) => (predicate: any) => {
      return find(getters.entities, predicate)
    },
  },
  mutations,
  state(): ModuleState {
    return {
      entities: {},
      loading: false,
    }
  },
}

export const factory = ({
  url,
  namespaced = true,
  autoFetchOnSignIn = false,
}: {
  url: string
  namespaced?: boolean
  autoFetchOnSignIn?: boolean
}): Module<ModuleState, RootState> => {
  return {
    ...module,
    actions: autoFetchOnSignIn ? module.actions : omit(module.actions, 'USER_SIGNED_IN'),
    namespaced,
    state: {
      entities: {},
      loading: false,
      url,
    },
  }
}

export default module
